/**
 * The customer's canvas, drawn flat for hanging on a photograph.
 *
 * Shared by the customer's own-wall photo (WallPhoto) and the shop's rooms
 * (RoomView), so a canvas looks the same on both - the same panels, the same
 * gap between them, the same shadow - rather than two drawings of one thing
 * that drift apart.
 */

import { useEffect, useState } from 'react';
import { useEditor } from '../state/store';
import type { CanvasSetup } from '../three/types';

/** Longest side of the design pictures used on a wall, px. */
const ART = 900;

/**
 * The design, one picture per page, redrawn whenever it changes.
 *
 * Pictures rather than a live canvas: the wall only needs to show the design,
 * and an image the browser already has decoded costs nothing to move about.
 */
export function useCanvasArt(): string[] {
  const doc = useEditor((s) => s.doc);
  const [art, setArt] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const { pageTexture } = await import('../three/texture');
      const current = useEditor.getState().doc;

      if (!current) return;

      const made: string[] = [];

      for (let index = 0; index < current.pages.length; index++) {
        const drawn = await pageTexture(current, index, ART);

        if (drawn) made.push(drawn.toDataURL('image/png'));
      }

      if (alive) setArt(made);
    })();

    return () => {
      alive = false;
    };
  }, [doc]);

  return art;
}

/** How wide the whole piece is across the wall, every panel and gap, mm. */
export function wholeWidth(canvas: CanvasSetup): number {
  const count = canvas.panels.count;

  return canvas.w * count + canvas.panels.gap * (count - 1);
}

/**
 * The panels, side by side, filling whatever box they are put in.
 *
 * Each panel's share of the width and the gaps between are worked out from
 * the millimetres, so a triptych with 50 mm gaps has 50 mm gaps whatever size
 * it is drawn at.
 */
export function CanvasPanels({ canvas, art }: { canvas: CanvasSetup; art: string[] }) {
  const count = canvas.panels.count;
  const whole = wholeWidth(canvas);
  const share = canvas.w / whole;
  const gapShare = canvas.panels.gap / whole;

  return (
    <>
      {Array.from({ length: count }, (_, panel) => {
        const picture = art[Math.min(panel, art.length - 1)];
        // One picture run across several panels: each shows its own slice of it.
        const across = art.length === 1 && count > 1;

        return (
          <span
            key={panel}
            className="tb-wall__panel"
            style={{
              width: `${share * 100}%`,
              marginRight: panel < count - 1 ? `${gapShare * 100}%` : 0,
              aspectRatio: `${canvas.w} / ${canvas.h}`,
              backgroundImage: picture ? `url(${picture})` : undefined,
              backgroundSize: across ? `${count * 100}% 100%` : 'cover',
              backgroundPosition: across ? `${(panel / (count - 1)) * 100}% 50%` : 'center',
            }}
          />
        );
      })}
    </>
  );
}
